import { useMemo } from 'react';
import { Bell } from '@/design-system/icons';
import { NotificationContainer } from './NotificationContainer';
import { sanitizeNotificationHtml } from '../lib/sanitizeNotificationHtml';
import { getNotificationState } from '../types';
import type { NotificationItem } from '../types';
import { useFormatRelativeTime } from '@/utils/timeUtils';

export interface GenericNotificationProps {
  notification: NotificationItem;
  onClick?: () => void;
  onDelete?: () => void;
}

// Fallback renderer for channels this frontend has no bespoke component for. It shows only what
// every inbox item carries — header and body — so new backend channels need no frontend release.
export function GenericNotification({ notification, onClick, onDelete }: GenericNotificationProps) {
  const state = getNotificationState(notification);
  const timestamp = useFormatRelativeTime(notification.created_at);
  const sanitizedBody = useMemo(
    () => sanitizeNotificationHtml(notification.body),
    [notification.body]
  );

  return (
    <NotificationContainer
      icon={
        <div className="size-8 flex items-center justify-center rounded-small bg-primary/10">
          <Bell className="size-4 text-primary" />
        </div>
      }
      timestamp={timestamp}
      state={state}
      onClick={onClick}
      onDelete={onDelete}
    >
      <p className="text-sm font-medium text-onsurface">{notification.header}</p>
      <div
        className="text-sm text-muted [&_a]:text-primary [&_a]:underline"
        dangerouslySetInnerHTML={{ __html: sanitizedBody }}
      />
    </NotificationContainer>
  );
}
